import type { z } from 'zod';
import { type TransformerConfig } from '../common/types';
import {
  createDecodeFromDbFn,
  createEncodeToDbFn,
  createRenameTransformer,
  createTransformer,
} from '../common/utils';
import { buildRecordId, decodeRecordId, encodeRecordId } from '../recordIds';
import { DBTutorialSchema, TutorialSchema } from './schemas';

type DBTutorial = z.infer<typeof DBTutorialSchema>;
type Tutorial = z.infer<typeof TutorialSchema>;

/** The stored sort key, `{startDatetime}#{suffix}`, from a tutorial's start and suffix. */
export const getTutorialStartDatetimeIdentifier = (
  tutorial: Pick<Tutorial, 'startDatetime' | 'suffix'>
) => `${tutorial.startDatetime}#${tutorial.suffix}`;

/** The start and suffix a stored sort key holds. */
export const parseTutorialStartDatetimeIdentifier = (startDatetimeIdentifier: string) => {
  const [startDatetime, suffix] = startDatetimeIdentifier.split('#');
  return { startDatetime: Number(startDatetime), suffix: suffix as string };
};

export const TutorialCodecConfig: TransformerConfig<
  typeof TutorialSchema,
  typeof DBTutorialSchema,
  'id'
> = {
  name: 'Tutorial',
  clientSchema: TutorialSchema,
  dbSchema: DBTutorialSchema,
  transformers: [
    createRenameTransformer<typeof TutorialSchema, typeof DBTutorialSchema>('courseId', 'course'),
    createRenameTransformer<typeof TutorialSchema, typeof DBTutorialSchema>('hostId', 'host'),

    // attendees: Attendee[] ↔ Record<studentId, Attendee>
    createTransformer<typeof TutorialSchema, typeof DBTutorialSchema>({
      encoder: {
        sourceFields: ['attendees'] as const,
        encode: ({ attendees }) => ({
          attendees: Object.fromEntries(
            (attendees as Tutorial['attendees']).map(val => [val.studentId, val])
          ),
        }),
      },
      decoder: {
        sourceFields: ['attendees'] as const,
        decode: ({ attendees }) => ({
          attendees: Object.values((attendees ?? {}) as NonNullable<DBTutorial['attendees']>),
        }),
      },
    }),

    // startDatetime, suffix ↔ startDatetimeIdentifier: `{startDatetime}#{suffix}`
    createTransformer<typeof TutorialSchema, typeof DBTutorialSchema>({
      encoder: {
        sourceFields: ['startDatetime', 'suffix'] as const,
        dependencies: ['startDatetime', 'suffix'] as const,
        encode: ({ startDatetime, suffix }) => ({
          startDatetimeIdentifier: getTutorialStartDatetimeIdentifier({
            startDatetime: startDatetime as Tutorial['startDatetime'],
            suffix: suffix as Tutorial['suffix'],
          }),
        }),
      },
      decoder: {
        sourceFields: ['startDatetimeIdentifier'] as const,
        decode: ({ startDatetimeIdentifier }) =>
          parseTutorialStartDatetimeIdentifier(startDatetimeIdentifier),
      },
    }),

    // linkedTutorialIds: [id] ↔ linkedTutorials: [startDatetimeIdentifier], all of one course
    createTransformer<typeof TutorialSchema, typeof DBTutorialSchema>({
      encoder: {
        sourceFields: ['linkedTutorialIds'] as const,
        // A full encode runs every encoder, and a tutorial outside a series has no list
        encode: ({ linkedTutorialIds }) =>
          linkedTutorialIds === undefined
            ? {}
            : {
                linkedTutorials: (
                  linkedTutorialIds as NonNullable<Tutorial['linkedTutorialIds']>
                ).map(tutorialId =>
                  getTutorialStartDatetimeIdentifier(decodeRecordId('tutorial', tutorialId))
                ),
              },
      },
      decoder: {
        sourceFields: ['course', 'linkedTutorials'] as const,
        // Each linked tutorial's id needs the course, so a read without it gives no list
        decode: ({ course, linkedTutorials }) =>
          course !== undefined && linkedTutorials !== undefined
            ? {
                linkedTutorialIds: linkedTutorials.map(startDatetimeIdentifier =>
                  encodeRecordId('tutorial', {
                    courseId: course,
                    ...parseTutorialStartDatetimeIdentifier(startDatetimeIdentifier),
                  })
                ),
              }
            : {},
      },
    }),
  ],
  derived: { id: tutorial => buildRecordId('tutorial', tutorial) },
};

export const encodeTutorialToDb = createEncodeToDbFn(TutorialCodecConfig);
export const decodeTutorialFromDb = createDecodeFromDbFn(TutorialCodecConfig);
